import BottomSheet from '@components/BottomSheet'
import { NftList } from '@components/NftList/NftList'
import Prompt from '@components/Prompt'
import { AccountAvatar } from '@modules/AccountList'
import { useAccountForm } from '@plug-core/hooks/useAccountForm'
import { Button, Field, Input, ListGroup, ListRow, Text } from '@ui'
import { ROUTES } from '@utils/routes'
import EmojiPicker, { Theme } from 'emoji-picker-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

export const AccountForm = () => {
  const { t } = useTranslation()
  const { principalId } = useParams<{ principalId: string }>()
  const { state } = useLocation()
  const navigate = useNavigate()

  const {
    name,
    setName,
    plugbookName,
    setPlugbookName,
    plugbookNameValidation,
    profilePic,
    setProfilePic,
    saveAccount,
    saveDisabled,
    isLoading,
    saveError,
    setSaveError,
  } = useAccountForm(principalId!, !!state?.isNewAccount)

  const [pictureTypePickerVisible, setPictureTypePickerVisible] = useState(false)
  const [emojiPickerVisible, setEmojiPickerVisible] = useState(false)
  const [nftPickerVisible, setNftPickerVisible] = useState(false)

  return (
    <div className="mt-6 mb-3 flex h-full flex-col gap-4">
      <button
        type="button"
        aria-label={t('accounts.selectPictureType')}
        className="cursor-pointer self-center rounded-full border-0 bg-transparent p-0 transition-transform duration-150 active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus motion-reduce:active:scale-100"
        onClick={() => setPictureTypePickerVisible(true)}
      >
        <AccountAvatar avatar={profilePic} size={100} background="paper" />
      </button>

      <Field label={t('accounts.plugbookName')} className="mt-6">
        <Text variant="body-s" tone="fg-2">
          {t('accounts.plugbookNameDescription')}
        </Text>
        <Input
          error={plugbookNameValidation ? !plugbookNameValidation.valid : false}
          value={plugbookName}
          onChange={(e) => setPlugbookName(e.target.value)}
        />
      </Field>

      <Field label={t('accounts.walletName')}>
        <Text variant="body-s" tone="fg-2">
          {t('accounts.walletNameDescription')}
        </Text>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      <Button
        loading={isLoading}
        disabled={saveDisabled}
        onClick={async () => {
          await saveAccount()

          if (state?.isNewAccount) {
            navigate(ROUTES.HOME)
          }
        }}
        variant="primary"
        fullWidth
      >
        {plugbookNameValidation?.error || t('common.save')}
      </Button>

      <BottomSheet
        visible={pictureTypePickerVisible}
        onBackdropClick={() => setPictureTypePickerVisible(false)}
        title={t('accounts.selectPictureType')}
      >
        <ListGroup>
          <ListRow
            primary={t('accounts.emojiPicture')}
            chevron
            onClick={() => {
              setPictureTypePickerVisible(false)
              setEmojiPickerVisible(true)
            }}
          />
          <ListRow
            primary={t('accounts.nftPicture')}
            chevron
            onClick={() => {
              setPictureTypePickerVisible(false)
              setNftPickerVisible(true)
            }}
          />
        </ListGroup>
      </BottomSheet>

      <BottomSheet
        visible={nftPickerVisible}
        onBackdropClick={() => setNftPickerVisible(false)}
        title={t('accounts.selectNft')}
      >
        <NftList
          onNftClick={(item) => {
            setNftPickerVisible(false)
            setProfilePic(item.url)
          }}
        />
      </BottomSheet>

      <EmojiPicker
        open={emojiPickerVisible}
        width={500}
        height={420}
        theme={Theme.DARK}
        style={{ position: 'absolute' }}
        previewConfig={{ showPreview: false }}
        onEmojiClick={(emoji) => {
          setProfilePic(emoji.emoji)
          setEmojiPickerVisible(false)
        }}
        skinTonesDisabled
      />

      <Prompt show={!!saveError} close={() => setSaveError('')}>
        <Text align="center" className="mt-2">
          {saveError}
        </Text>

        <Button size="sm" variant="secondary" onClick={() => setSaveError('')}>
          {t('common.understood')}
        </Button>
      </Prompt>
    </div>
  )
}
